import React from 'react'
import {
  OverviewIcon,
  OrdersIcon,
  AppsIcon as ProductsIcon,
  HelpIcon,
  LocationIcon,
  TagIcon as DiscountsIcon,
  CustomersIcon,
  PuzzleIcon as DesignerIcon,
  BoltIcon as LoyaltyIcon,
  ComposerIcon,
  SlerpLogo,
  ChevronRightIcon,
  ChevronLeftIcon,
  LogOutIcon,
  WindowIcon,
  AppCampaignIcon,
  SettingsIcon,
  CRMIcon,
  DispatchIcon
} from '@slerp/assets'
import Menu from 'antd/lib/menu'
import Layout from 'antd/lib/layout'
import Divider from 'antd/lib/divider'
import { Link } from 'react-router-dom'
import { ASSET_HOST, HELP_CENTRE_URL } from '@slerp/client'
import { isAdmin, isManager, useSession } from '@slerp/accounts'
import { useGlobalQuery } from 'hooks/useGlobalQuery'
import { crmUrl } from './utils/features'
import { handleComposerSSO, allowComposerSSO } from './../Composer/utils'
import StreamRedirect from './StreamRedirect'
import { SignOut } from 'components/Utils/session'
import env from 'env'

const { Sider } = Layout

const Sidebar = () => {
  const { merchant, user } = useSession()
  const { merchantSetting, setCollapsed, collapsed } = useGlobalQuery()

  const merchantStoreUrl = `https://${merchant?.slug}.${env.ASSET_HOST}`

  const currentActiveTabChecker = () => {
    const currentPath = window.location.pathname

    if (currentPath.indexOf('/analytics') === 0) return 'home'
    if (currentPath.indexOf('/heat-map') === 0) return 'home'
    if (currentPath.indexOf('/order') === 0) return 'orders'
    if (currentPath.indexOf('/location') === 0) return 'locations'
    if (currentPath.indexOf('/product') === 0) return 'products'
    if (currentPath.indexOf('/modifiers') === 0) return 'products'
    if (currentPath.indexOf('/discount') === 0) return 'discounts'
    if (currentPath.indexOf('/customer') === 0) return 'customers'
    if (currentPath.indexOf('/loyalty') === 0) return 'loyalty'
    if (currentPath.indexOf('/designer') === 0) return 'designer'
    if (currentPath.indexOf('/designer/mobile-app') === 0) return 'designer'
    if (currentPath.indexOf('/designer/content') === 0) return 'designer'
    if (currentPath.indexOf('/settings') === 0) return 'settings'
    if (currentPath.indexOf('/app-campaigns') === 0) return 'app-campaigns'
    if (currentPath.indexOf('/composer') === 0) return 'composer'
    if (currentPath.indexOf('/slerp-menu-manager') === 0) return 'stream'
    if (currentPath.indexOf('/settings/general') === 0)
      return 'general-settings'
    return 'home'
  }

  const toggle = () => {
    setCollapsed((prev: boolean) => !prev)
  }

  const hasAdminRole = isAdmin(user)
  const hasManagerRole = isManager(user)

  return (
    <Sider
      data-testid='layout-sidebar-component'
      trigger={null}
      collapsible
      width={210}
      collapsedWidth={66}
      collapsed={collapsed}
    >
      <Menu
        mode='inline'
        defaultSelectedKeys={[currentActiveTabChecker()]}
        className='sidebar-nav'
      >
        <Menu.Item
          key='merchant-details'
          disabled
          className='merchant-details-container default-cursor'
        >
          <Link to='' className='sidebar-tooltip'>
            <SlerpLogo className='anticon' />
            <span className='merchant-details'>
              <span className='slug'>{merchant?.name}</span>
              <div className='user-info'>
                <span className='name'>{user?.firstname}</span>|
                <span className='role'>{hasAdminRole ? 'Admin' : 'Staff'}</span>
              </div>
            </span>
          </Link>
        </Menu.Item>
        <div style={{ padding: '0 5px' }}>
          <Divider style={{ margin: 0 }} />
        </div>
        <Menu.Item key='home'>
          <Link to='/analytics' className='sidebar-tooltip'>
            <OverviewIcon className='anticon' />
            <span>Overview</span>
          </Link>
        </Menu.Item>
        <Menu.Item key='orders' hidden={!hasAdminRole}>
          <Link to='/orders' className='sidebar-tooltip'>
            <OrdersIcon className='anticon' />
            <span>Orders</span>
          </Link>
        </Menu.Item>
        <Menu.Item key='locations'>
          <Link to='/locations' className='sidebar-tooltip'>
            <LocationIcon className='anticon' />
            <span>Locations</span>
          </Link>
        </Menu.Item>
        <Menu.Item key='products' hidden={!hasAdminRole}>
          <Link to='/products' className='sidebar-tooltip'>
            <ProductsIcon className='anticon' />
            <span>Products</span>
          </Link>
        </Menu.Item>
        <Menu.Item key='discounts' hidden={!hasAdminRole}>
          <Link to='/discounts' className='sidebar-tooltip'>
            <DiscountsIcon className='anticon' />
            <span>Discounts</span>
          </Link>
        </Menu.Item>
        <Menu.Item key='customers' hidden={!hasAdminRole}>
          <Link to='/customers' className='sidebar-tooltip'>
            <CustomersIcon className='anticon' />
            <span>Customers</span>
          </Link>
        </Menu.Item>
        <Menu.Item key='loyalty' hidden={!hasAdminRole}>
          <Link to='/loyalty' className='sidebar-tooltip'>
            <LoyaltyIcon className='anticon' style={{ fill: 'transparent' }} />
            <span>Loyalty</span>
          </Link>
        </Menu.Item>
        <Menu.Item key='designer' hidden={!hasAdminRole}>
          <Link to='/designer' className='sidebar-tooltip'>
            <DesignerIcon className='anticon' />
            <span>Designer</span>
          </Link>
        </Menu.Item>
        {merchantSetting && !allowComposerSSO(merchantSetting) && (
          <Menu.Item
            key='composer'
            className='sidebar-composer'
            hidden={!hasAdminRole}
          >
            <Link to='/composer' className='sidebar-tooltip'>
              <ComposerIcon
                className='anticon'
                style={{ fill: 'transparent' }}
              />
              <span>Composer</span>
            </Link>
          </Menu.Item>
        )}
        {merchantSetting && allowComposerSSO(merchantSetting) && (
          <Menu.Item
            key='composer'
            className='sidebar-composer'
            hidden={!hasAdminRole}
          >
            <a
              onClick={() => handleComposerSSO(merchantSetting)}
              className='sidebar-tooltip'
            >
              <ComposerIcon
                className='anticon'
                style={{ fill: 'transparent' }}
              />
              <span>Composer</span>
            </a>
          </Menu.Item>
        )}
        <Menu.Item
          key='dispatch'
          hidden={!merchantSetting?.dispatch_selfserve_enabled}
        >
          <a
            href={`https://${merchant?.slug}.${ASSET_HOST}/controls/dispatch?token=${user?.api_key}`}
            target='_blank'
            className='sidebar-tooltip'
            rel='noreferrer'
          >
            <DispatchIcon className='anticon' style={{ fill: 'transparent' }} />
            <span>Dispatch</span>
          </a>
        </Menu.Item>
        {(merchantSetting || hasAdminRole) && (
          <Menu.Item
            key='integrations'
            className='section-label default-cursor'
            disabled
          >
            <a className='sidebar-section-label'>
              {collapsed ? 'Int.' : 'Integrations'}
            </a>
          </Menu.Item>
        )}
        {merchantSetting && (
          <Menu.Item key='crm' className='sidebar-crm' hidden={!hasAdminRole}>
            <a
              href={crmUrl(merchantSetting)}
              className='sidebar-tooltip'
              target='_blank'
              rel='noreferrer'
            >
              <CRMIcon className='anticon' />
              <span>Slerp CRM</span>
            </a>
          </Menu.Item>
        )}
        <Menu.Item key='app-campaigns' hidden={!hasAdminRole}>
          <Link
            to={
              merchantSetting?.app_setting?.enabled
                ? '/app-campaigns'
                : '/designer/mobile-app'
            }
            className='sidebar-tooltip'
          >
            <AppCampaignIcon className='anticon' />
            <span style={{ whiteSpace: 'nowrap' }}>App Campaigns</span>
          </Link>
        </Menu.Item>
        {(hasAdminRole || hasManagerRole) && <StreamRedirect />}
        <div style={{ padding: '0 5px' }} className='sidebar-settings'>
          <Divider style={{ margin: 0 }} />
        </div>
        {hasAdminRole && (
          <Menu.Item key='general-settings'>
            <Link to='/settings/general' className='sidebar-tooltip'>
              <SettingsIcon className='anticon' />
              <span>Settings</span>
            </Link>
          </Menu.Item>
        )}
        <Menu.Item key='help-centre'>
          <a
            href={HELP_CENTRE_URL}
            target='_blank'
            rel='noopener noreferrer'
            className='sidebar-tooltip'
          >
            <HelpIcon className='anticon' />
            <span>Help Centre</span>
          </a>
        </Menu.Item>
        <Menu.Item key='sign-out'>
          <a href='/' className='sidebar-tooltip' onClick={SignOut}>
            <LogOutIcon className='anticon' style={{ fill: 'transparent' }} />
            <span>Log Out</span>
          </a>
        </Menu.Item>
        <div style={{ padding: '0 5px' }}>
          <Divider style={{ margin: 0 }} />
        </div>
        <Menu.Item key='window' id='window'>
          <a
            href={merchantStoreUrl}
            rel='noreferrer'
            className='sidebar-tooltip'
            target='_blank'
          >
            <WindowIcon className='anticon' />
            <span>Visit your site</span>
          </a>
        </Menu.Item>
        <Menu.Item key='collapse-menu' onClick={toggle}>
          <Link to='#' className='sidebar-tooltip'>
            {collapsed ? (
              <ChevronRightIcon
                className='anticon'
                style={{ fill: 'transparent' }}
              />
            ) : (
              <ChevronLeftIcon
                className='anticon'
                style={{ fill: 'transparent' }}
              />
            )}
            <span>Collapse Menu</span>
          </Link>
        </Menu.Item>
      </Menu>
    </Sider>
  )
}

export default Sidebar
